const CHART_JS_URL = "https://cdn-eu.seatsio.net/chart.js";
const CHART_JS_SCRIPT_ID = "chartScript";
const CHART_JS_LOAD_TIMEOUT_MS = 20000;

/**
 * Load seats.io chart.js, resolving once `window.seatsio` is usable.
 * The global is the only reliable "loaded" signal: a script tag can exist for a
 * failed load. The retry busts poisoned browser/proxy caches.
 */
export async function loadSeatsioChartJs(): Promise<void> {
  if ((window as any).seatsio) {
    return;
  }

  const urls = [CHART_JS_URL, `${CHART_JS_URL}?retry=${Date.now()}`];
  let lastError: unknown = null;

  for (const url of urls) {
    try {
      await loadChartJsScript(url);
    } catch (err) {
      lastError = err;
      continue;
    }
    if ((window as any).seatsio) {
      return;
    }
    lastError = new Error("seatsio global unavailable after loading chart.js");
  }
  throw lastError;
}

function loadChartJsScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Drop any tag left by a previous failed load
    document.getElementById(CHART_JS_SCRIPT_ID)?.remove();

    const script = document.createElement("script");
    script.id = CHART_JS_SCRIPT_ID;
    script.src = url;
    script.async = true;

    // A hung request must fail instead of loading forever
    const timeoutId = setTimeout(() => {
      script.remove();
      reject(new Error(`Loading ${url} timed out`));
    }, CHART_JS_LOAD_TIMEOUT_MS);

    script.onload = () => {
      clearTimeout(timeoutId);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timeoutId);
      script.remove();
      reject(new Error(`Loading ${url} failed`));
    };
    document.body.appendChild(script);
  });
}
